import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { DocumentProposal } from '../api/types.js';
import { useDecideDocument, useDocumentProposals } from '../hooks/use-document-proposals.js';
import { Button } from './button.js';

// « Document changes »: what the server's sessions ask to change in a Doc, Sheet or Slides that aidev did not create
// (ADR-038 section 2b). Nothing is written until approved here. Each card shows the change itself, the way it will
// land in the file, so it can be judged without opening the document. The texts are untrusted: plain text only.

const VERB: Record<DocumentProposal['change']['kind'], string> = {
  doc_append: 'Add text to', doc_replace: 'Replace a passage in', sheet_values: 'Write cells in', slides_add: 'Add slides to',
};
const EDGE: Record<DocumentProposal['change']['kind'], string> = {
  doc_append: 'var(--color-brand)', doc_replace: 'var(--color-warn)', sheet_values: 'var(--color-warn)', slides_add: 'var(--color-brand)',
};
const muted = { color: 'var(--color-text-3)' };

/** The text as the Doc will show it: headings and bullets from their marks */
function DocText({ text }: { text: string }) {
  return (
    <div className="mt-2 max-h-64 overflow-y-auto border-l-2 pl-3 text-sm" style={{ borderColor: 'var(--color-border)' }}>
      {text.split('\n').map((line, i) => {
        const h = /^(#{1,3})\s+(.*)$/.exec(line);
        const b = /^[-*]\s+(.*)$/.exec(line);
        if (h) return <p key={i} className={`${h[1]!.length === 1 ? 'text-base' : 'text-sm'} mt-1 font-semibold break-words`} style={{ color: 'var(--color-text)' }}>{h[2]}</p>;
        if (b) return <p key={i} className="break-words pl-4 -indent-3" style={{ color: 'var(--color-text)' }}>• {b[1]}</p>;
        return line.trim() ? <p key={i} className="break-words" style={{ color: 'var(--color-text)' }}>{line}</p> : <div key={i} className="h-2" aria-hidden="true" />;
      })}
    </div>
  );
}

function Cells({ rows, label }: { rows: unknown[][]; label: string }) {
  return (
    <div className="min-w-0">
      <p className="text-xs" style={muted}>{label}</p>
      <div className="mt-1 max-h-56 overflow-auto">
        <table className="border-collapse text-xs tabular-nums">
          <tbody>
            {rows.slice(0, 50).map((r, i) => (
              <tr key={i}>{r.slice(0, 20).map((c, j) => <td key={j} className="border px-1.5 py-0.5 align-top whitespace-nowrap" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text)' }}>{String(c ?? '')}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** The change, as it will land in the file */
function Change({ p }: { p: DocumentProposal }) {
  const c = p.change;
  if (c.kind === 'doc_append') return <DocText text={c.text} />;
  if (c.kind === 'doc_replace') {
    const n = p.before?.occurrences;
    return (
      <div className="mt-2 space-y-1 text-sm">
        <p className="break-words" style={muted}><span className="sr-only">Replaced: </span><s>{c.find}</s></p>
        <p className="break-words" style={{ color: 'var(--color-text)' }}><span className="sr-only">By: </span>{c.text || <em style={muted}>nothing (the passage is removed)</em>}</p>
        {n !== undefined && <p className="text-xs" style={muted}>{n === 1 ? 'Once in the document.' : `${n} times in the document, all replaced.`}</p>}
      </div>
    );
  }
  if (c.kind === 'sheet_values') {
    const now = p.before?.rows ?? [];
    return (
      <div className="mt-2">
        <p className="text-xs" style={muted}>From <code style={{ fontFamily: 'var(--font-mono)' }}>{c.range}</code></p>
        <div className="mt-1 grid gap-3 sm:grid-cols-[auto_auto] sm:justify-start sm:gap-8">
          {now.length > 0 ? <Cells rows={now} label="Now" /> : <p className="text-xs" style={muted}>These cells are empty now.</p>}
          <Cells rows={c.rows} label="Becomes" />
        </div>
      </div>
    );
  }
  return (
    <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm">
      {c.slides.map((s, i) => (
        <li key={i} className="break-words">
          <span className="font-medium" style={{ color: 'var(--color-text)' }}>{s.title || <em style={muted}>no title</em>}</span>
          {s.body && <span className="block whitespace-pre-line" style={{ color: 'var(--color-text-2)' }}>{s.body}</span>}
        </li>
      ))}
    </ol>
  );
}

function Outcome({ p }: { p: DocumentProposal }) {
  if (p.status === 'applied') return <p className="text-sm" style={{ color: 'var(--color-success)' }}>Applied to the document.{p.link && <> <a href={p.link} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>Open it</a></>}</p>;
  if (p.status === 'rejected') return <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>Rejected: the document is unchanged.</p>;
  if (p.status === 'failed') return <p className="text-sm" style={{ color: 'var(--color-error)' }}>Approved, but not applied: {p.error}</p>;
  if (p.status === 'applying') return <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>Applying…</p>;
  return null;
}

export function DocumentProposalCard({ serverId, p, isAdmin, focused, onDecided, inSession = false }: { serverId: string; p: DocumentProposal; isAdmin: boolean; focused: boolean; onDecided: (id: string) => void; inSession?: boolean }) {
  const decide = useDecideDocument(serverId);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }, [focused]);
  const error = decide.error as Error | null;
  const id = `document-${p.proposalId}`;
  return (
    <article ref={ref} id={id} aria-labelledby={`${id}-title`} className="card px-4 py-3"
      style={{ borderLeft: `3px solid ${EDGE[p.change.kind]}`, ...(focused ? { outline: '2px solid var(--color-brand-light)', outlineOffset: '3px' } : {}) }}>
      <h3 id={`${id}-title`} className="text-sm font-semibold break-words" style={{ color: 'var(--color-text)' }}>
        {VERB[p.change.kind]}{' '}
        {p.link ? <a href={p.link} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>{p.fileName || 'the file'}</a> : p.fileName}
      </h3>
      <Change p={p} />
      {(p.reason || (p.requestedBySession && !inSession)) && (
        <p className="mt-2 text-xs break-words" style={{ color: 'var(--color-text-2)' }}>
          {p.reason && <>Why: {p.reason}. </>}
          {p.requestedBySession && !inSession && <>Asked by <Link to={`/servers/${serverId}/sessions/${p.requestedBySession}`} className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>a session</Link>.</>}
        </p>
      )}
      <div className="mt-3">
        {p.status === 'pending' && !decide.isSuccess ? (
          isAdmin ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" className="max-sm:min-h-10" disabled={decide.isPending}
                onClick={() => decide.mutate({ proposalId: p.proposalId, decision: 'approve' }, { onSuccess: () => onDecided(p.proposalId) })}>
                {decide.isPending && decide.variables?.decision === 'approve' ? 'Applying…' : 'Apply to the document'}
              </Button>
              <Button size="sm" className="max-sm:min-h-10" variant="ghost" disabled={decide.isPending}
                onClick={() => decide.mutate({ proposalId: p.proposalId, decision: 'reject' }, { onSuccess: () => onDecided(p.proposalId) })}>
                Reject
              </Button>
            </div>
          ) : <p className="text-xs" style={muted}>Waiting for an admin of this server to decide.</p>
        ) : <Outcome p={decide.data?.proposal ?? p} />}
        {/* A change Google refused (a connection or a file to fix first): applied again on the same click */}
        {isAdmin && (decide.data?.proposal ?? p).status === 'failed' && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button size="sm" className="max-sm:min-h-10" disabled={decide.isPending}
              onClick={() => decide.mutate({ proposalId: p.proposalId, decision: 'approve' })}>
              {decide.isPending && decide.variables?.decision === 'approve' ? 'Applying…' : 'Try again'}
            </Button>
            <Button size="sm" className="max-sm:min-h-10" variant="ghost" disabled={decide.isPending}
              onClick={() => decide.mutate({ proposalId: p.proposalId, decision: 'reject' }, { onSuccess: () => onDecided(p.proposalId) })}>
              Reject
            </Button>
          </div>
        )}
        {error && <p role="alert" className="mt-2 text-xs" style={{ color: 'var(--color-error)' }}>Not decided: {error.message}</p>}
      </div>
    </article>
  );
}

const decidedLine = (p: DocumentProposal) => `${VERB[p.change.kind]} ${p.fileName || 'a file'}, ${p.status === 'failed' ? `failed: ${p.error ?? ''}` : p.status}`;

/** The changes to decide, then the ones decided in the last 7 days, folded; nothing when there is none */
export function DocumentChanges({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const { data } = useDocumentProposals(serverId);
  const [params] = useSearchParams();
  const focus = params.get('document');
  const [decidedHere, setDecidedHere] = useState<Set<string>>(() => new Set());
  if (!data?.length) return null;
  // A change approved but not applied waits too: to try again
  // A session's changes are decided in that session (ADR-039 8c); one still opened by its old link stays here
  const pending = data.filter((p) => (p.status === 'pending' || p.status === 'applying' || p.status === 'failed' || decidedHere.has(p.proposalId)) && (!p.requestedBySession || p.proposalId === focus));
  const decided = data.filter((p) => !pending.includes(p));
  if (!pending.length && !decided.some((p) => p.proposalId === focus)) return null;
  return (
    <section aria-labelledby="documents-title" className="mb-8 min-w-0">
      <h2 id="documents-title" className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>Document changes</h2>
      <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-2)' }}>
        {pending.some((p) => p.status === 'pending' || p.status === 'failed')
          ? 'Asked by this server\'s sessions, in files aidev did not create. Nothing is written until you apply it here.'
          : 'Nothing waits for your decision.'}
      </p>
      {pending.length > 0 && (
        <div className="mt-3 space-y-3">
          {pending.map((p) => <DocumentProposalCard key={p.proposalId} serverId={serverId} p={p} isAdmin={isAdmin} focused={p.proposalId === focus} onDecided={(id) => setDecidedHere((s) => new Set(s).add(id))} />)}
        </div>
      )}
      {decided.length > 0 && (
        <details className="mt-3" open={decided.some((p) => p.proposalId === focus) || undefined}>
          <summary className="cursor-pointer py-1.5 text-xs max-sm:min-h-10" style={muted}>Decided in the last 7 days ({decided.length})</summary>
          <ul className="mt-2 space-y-1">
            {decided.map((p) => (
              <li key={p.proposalId} id={`document-${p.proposalId}`} className="text-xs break-words" style={{ color: p.status === 'failed' ? 'var(--color-error)' : 'var(--color-text-2)' }}>{decidedLine(p)}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
